import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { devApi, type DevUser } from "../api/dev";
import { useAuth } from "../auth/AuthContext";
import { Select, type SelectOption } from "./Select";

/**
 * Dev-only account switcher in the top bar: pick a user and the session becomes theirs, no
 * logout/login. Rendered only in a Vite dev build and only when the backend answers
 * `/api/dev/users` (profile `dev`); anywhere else it renders nothing.
 */
export function DevUserSwitcher() {
  const { state, refresh } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<DevUser[] | null>(null);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    devApi.users()
      .then((list) => { if (!cancelled) setUsers(list); })
      .catch(() => { /* backend not on profile dev — no switcher */ });
    return () => { cancelled = true; };
  }, []);

  const options = useMemo<SelectOption<string>[]>(
    () => (users ?? []).map((u) => {
      const name = [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email;
      const roles = u.roles.map((r) => r.name).join(", ");
      return { value: u.id, label: roles ? `${name} · ${roles}` : name, keywords: u.email };
    }),
    [users],
  );

  if (!users || users.length === 0 || state.status !== "authenticated") return null;
  const currentId = state.user.id;

  async function handleChange(id: string) {
    if (id === currentId || switching) return;
    setSwitching(true);
    try {
      await devApi.switchUser(id);
      await refresh();
      // The page just left may be closed to the new user — start from Početna (it redirects on).
      navigate("/", { replace: true });
    } finally {
      setSwitching(false);
    }
  }

  return (
    <Select
      value={currentId}
      options={options}
      onChange={(id) => void handleChange(id)}
      disabled={switching}
      ariaLabel="Promeni korisnika (dev)"
      style={{ width: "var(--dev-switcher-w, 260px)" }}
    />
  );
}
